import React, { useEffect, useMemo, useState } from "react"
import { Container } from "reactstrap"
import { Link, useLocation, useNavigate } from "react-router-dom"
import ListingTabs from "./ListingTabs"
import { isPermitted, firstPermittedIndex } from "../Functions/permissions"
import { tabIdForPath, goToTab } from "../Functions/pathTabs"
// Reuses the codebase's one "insufficient permissions" look (see Components/RestrictedSection.js)
// rather than importing that component directly: RestrictedSection is a HOC wired to its own
// single-permission network-only query, which is the right shape for gating one panel but
// doesn't fit here - a merged page can have several tabs, each with its own permission, and
// this message covers "none of them", not one named requiredPermission. Matching its copy and
// classes (via the same stylesheet) keeps the two visually identical without forcing a second,
// redundant permissions fetch just to render text.
import "../Components/RestrictedSection.css"
import "./TabbedListingPage.css"

// One shell for every tabbed listing page: renders the tab strip, keeps the active tab in the
// path (Layout/routes.js's `route`), locks tabs the user lacks permission for, and lands on
// the first tab the user can actually open.
//
// The active tab is read from and written to react-router's own location (useLocation() /
// useNavigate()) rather than window.location, so react-router's location (read by
// useLocation() everywhere else in the tree, e.g. Filters/filterUrl.js and this component
// itself) stays the single source of truth for which tab is active. That also makes
// Back/Forward work correctly: switching tabs always changes `?filter=` too (see
// `clearFilters` below), so a Back press moves between two history entries that differ in
// both `pathname` and `search` in one step, and only a listener keyed on the router's own
// location (which updates unconditionally on `popstate`) is guaranteed to catch that.
const TabbedListingPage = ({ tabs = [], hasPermissions, loading = false, className = "", onTabChange, route }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [activeTabId, setActiveTabId] = useState(null)

    const hasGatedTabs = useMemo(() => tabs.some(tab => !!tab.permissionRequired), [tabs])
    // Only a gated page needs to wait: an ungated page can pick its tab straight away.
    const waitingOnPermissions = hasGatedTabs && loading

    const tabInUrl = tabs.find(tab => tab.id === tabIdForPath(route, location.pathname))
    const writeTab = (tab, options) => goToTab(navigate, location, route, tab.id, options)

    // Keyed on react-router's own location (not window.location), so this also re-runs on
    // browser Back/Forward and on a sidebar click that changes only the tab segment.
    useEffect(() => {
        if (waitingOnPermissions || tabs.length === 0) return

        const target = tabInUrl && isPermitted(tabInUrl, hasPermissions)
            ? tabInUrl
            : tabs[firstPermittedIndex(tabs, hasPermissions)]

        if (!target) return
        // A real switch away from a tab this shell had already settled on (not the very
        // first resolution on mount, and not just re-affirming the same tab after e.g. a
        // permissions refetch) -- the outgoing tab's advanced filters don't apply here.
        const isTabSwitch = activeTabId !== null && target.id !== activeTabId
        if (tabInUrl?.id !== target.id) writeTab(target, { replace: true, clearFilters: isTabSwitch })
        if (target.id !== activeTabId) {
            setActiveTabId(target.id)
            onTabChange?.(target)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingOnPermissions, hasPermissions, location.pathname])

    const changeTab = (tabId) => {
        const tab = tabs.find(item => item.id === tabId)
        if (!tab || !isPermitted(tab, hasPermissions) || tab.id === activeTabId) return
        writeTab(tab, { clearFilters: true })
        setActiveTabId(tab.id)
        onTabChange?.(tab)
    }

    if (waitingOnPermissions) return <Container fluid className={className} />

    const decoratedTabs = tabs.map(tab => ({ ...tab, disabled: !isPermitted(tab, hasPermissions) }))
    const activeTab = tabs.find(tab => tab.id === activeTabId)
    // A merged page whose every tab is gated, viewed by a user who holds none of those
    // permissions, used to fall through to an empty body below the tab strip - previously
    // that same user would never have reached the page at all (the whole sidebar group was
    // hidden). Now the nav item is reachable but locked-looking; show the standard
    // insufficient-permissions message instead of a page that looks broken.
    const noTabPermitted = tabs.length > 0 && !tabs.some(tab => isPermitted(tab, hasPermissions))

    return (
        <Container fluid className={`bos-tabbed-listing-page ${className}`.trim()}>
            <ListingTabs tabs={decoratedTabs} activeTab={activeTabId} onChange={changeTab} />
            {noTabPermitted ?
                <div className="permission-box">
                    <div className="permission-box-title">
                        <h4> Insufficient Permissions&nbsp;&nbsp;<i className="fa fa-meh-o" aria-hidden="true"></i></h4>
                    </div>
                    <div className="permission-box-body">
                        <p className="permission-box-message">You don't have enough permissions to access this page. Please contact the admin for further assistance.</p>
                    </div>
                    <br /><br />
                    <div className="permission-box-footer">
                        <Link className="permission-box-button btn btn-primary" to="/">
                            Go To Homepage
                        </Link>
                    </div>
                </div> :
                activeTab && isPermitted(activeTab, hasPermissions) && activeTab.render()
            }
        </Container>
    )
}

export default TabbedListingPage
